import { useLazyQuery } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';

import { ScrollBoxAttr } from '~/components/blocks';
import {
  GetPatientAppointmentsDocument,
  HistoryAppointmentFragment,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { usePreviousValue } from '~/hooks/use-previous-value';
import { historyPageInfoState } from '~/state/partials/patient_karte/atoms';

type Appointments = {
  loading: boolean;
  appointments: HistoryAppointmentFragment[];
};

export const useFetchAppointments = (
  scrollRef: React.MutableRefObject<ScrollBoxAttr | null>,
): Appointments => {
  const [{ patientId, page, perPage }, setPageInfo] = useAtom(historyPageInfoState);

  const [getPatientAppointments, { data, loading }] = useLazyQuery(
    GetPatientAppointmentsDocument,
    {},
  );

  const prevData = usePreviousValue(data);
  const currentData = data || prevData;
  const appointments = useMemo(
    () => getNode(currentData, 'Patient')?.appointments.nodes || [],
    [currentData],
  );

  useEffect(() => {
    if (patientId) {
      getPatientAppointments({
        variables: {
          patientId,
          page,
          perPage,
        },
      });
    }
  }, [getPatientAppointments, page, patientId, perPage]);

  useEffect(() => {
    if (!data) return;

    const totalPage = getNode(data, 'Patient')?.appointments.pagesCount ?? 1;
    setPageInfo((_state) => ({ ..._state, totalPage }));

    scrollRef.current?.toTop();
  }, [data, setPageInfo, scrollRef]);

  return { loading, appointments };
};
